import { lazy, Suspense, useEffect, useState } from 'react'
import {
    Route,
    BrowserRouter as Router,
    Routes,
    useNavigate,
    useSearchParams,
    Navigate,
    useLocation,
} from 'react-router-dom'
import LoadingSpinner from './uiV2/common/loading/LoadingSpinner'
import { useUserDataStore } from './stateStores/User'
import tenantApi from './api/tenant/TenantApi'
import { Pages } from './Pages'
import Logout from './pages/Logout'
import Users from './pages/Users'
import ManageTenant from './pages/ManageTenant'
import CreateTenant from './pages/CreateTenant'
import CreateUser from './pages/CreateUser'
import UpdateUser from './pages/UpdateUser'
import ViewUser from './pages/ViewUser'
import { UserRoles } from './api/tenant/models/UserDto'
import { AuthHelper } from './auth/AuthHelper'
import AccessDenied from './pages/AccessDenied'
import Note from './components/Note'
import { NoteType } from './enums/NoteType'
import BlankLayout from './components/BlankLayout'
import NotFound404 from './pages/NotFound404'
import UpdateRole from './uiV2/role/updateRole/UpdateRole'
import DataManagement from './pages/DataManagement'
import Kit from './pages/Kit'
import KitConvoDebug from './pages/KitConvoDebug'
import { ManageCandidateProvider } from './components/manageRole/stateStore/ManageCandidateProvider'
import axios from 'axios'
import Login from './uiV2/login/Login'
import SignUp from './uiV2/login/SignUp'
import SignUpLinkedIn from './uiV2/login/SignUpLinkedIn'
import SignUpLinkedInConfirm from './uiV2/login/SignUpLinkedInConfirm'
import Invite from './uiV2/login/Invite'
import RoleList from './uiV2/role/roleList/RoleList'
import CreateRole from './uiV2/role/createRole/CreateRole'
import ManageRoleHM from './uiV2/role/manageRole/ManageRoleHM'
import CandidateReview from './uiV2/role/candidateReview/CandidateReview'
// import ManageRole from './uiV2/role/manageRole/ManageRole'
import { CandidateReviewStatus } from './api/role/models/CandidateReviewStatus'
import roleApi from './api/role/RoleApi'
import ManageRoleTA from './pages/ManageRole'
import LLMSettings from './pages/LLMSettings'
import CreateRoleLLM from './uiV2/role/createRole/CreateRoleLLM'
import { AuthProvider } from './auth/AuthProvider'
import { useAuth } from './auth/AuthContext'
import MagicLogin from './uiV2/login/MagicLogin'
import HelixSearchV2Copy from './uiV2/helixSearchV2Copy/HelixSearchV2Copy'
import { PosthogIdentitySync } from './analytics/PosthogIdentitySync'
import CandidateInterview from './uiV2/candidate/CandidateInterview'
import CandidateLandingOption2 from './uiV2/candidate/CandidateLandingOption2'
import CandidateApply from './uiV2/candidate/apply/CandidateApply'
import CandidateApplyKit from './uiV2/candidate/apply/CandidateApplyKit'
import KitTest from './uiV2/kitTest/KitTest'
import Jobs from './pages/Jobs'
import JobScreenersList from './uiV2/jobScreeners/JobScreenersList'
import JobScreenerDetail from './uiV2/jobScreeners/JobScreenerDetail'
import HiringManagerKitSignup from './uiV2/hiringManager/HiringManagerKitSignup'
import HiringManagerKit from './uiV2/hiringManager/HiringManagerKit'
import ShareInviteFallback from './uiV2/candidate/ShareInviteFallback'
import CandidateLogin from './uiV2/candidate/login/CandidateLogin'
import CandidateMagicLogin from './uiV2/candidate/login/CandidateMagicLogin'
const LlmRoutes = lazy(() => import('./llm/LlmRoutes'))
const LlmTestPage = lazy(() => import('./llm/LlmTestPageWrapper'))

const DEFAULT_JOB_URL_ID = 'social-media-manager-1'

function App(): React.JSX.Element {
    return (
        <Router>
            <AuthProvider>
                <PosthogIdentitySync />
                <Routes>
                    {/* public routes */}
                    <Route path={Pages.Login} element={<Login />} />
                    <Route
                        path={Pages.CandidateLogin}
                        element={<CandidateLogin />}
                    />
                    <Route path={Pages.Signup} element={<SignUp />} />
                    <Route path={Pages.Invite} element={<Invite />} />
                    <Route path={Pages.MagicLogin} element={<MagicLogin />} />
                    <Route
                        path={Pages.CandidateMagicLogin}
                        element={<CandidateMagicLogin />}
                    />
                    <Route
                        path={Pages.CandidateLandingOption1}
                        element={
                            <RedirectWithQuery
                                to={Pages.CandidateLandingOption2}
                            />
                        }
                    />
                    <Route
                        path={Pages.CandidateLanding}
                        element={
                            <RedirectWithQuery
                                to={Pages.CandidateLandingOption2}
                            />
                        }
                    />
                    <Route
                        path={Pages.CandidateLandingOption2}
                        element={<CandidateLandingOption2 />}
                    />
                    <Route
                        path={Pages.CandidateSignUp}
                        element={
                            <RedirectWithQuery
                                to={Pages.CandidateLandingOption2}
                            />
                        }
                    />
                    <Route
                        path={Pages.CandidateInterview}
                        element={<CandidateInterview />}
                    />
                    <Route
                        path={Pages.CandidateApply}
                        element={
                            <Navigate
                                to={`${Pages.CandidateApply}/${DEFAULT_JOB_URL_ID}`}
                                replace
                            />
                        }
                    />
                    <Route
                        path={Pages.CandidateApplyDynamic}
                        element={<CandidateApply />}
                    />
                    <Route
                        path={Pages.CandidateApplyKit}
                        element={<CandidateApplyKit />}
                    />
                    <Route
                        path={Pages.CandidateApplyKitDynamic}
                        element={<CandidateApplyKit />}
                    />
                    <Route
                        path={Pages.HiringManagerKitSignup}
                        element={<HiringManagerKitSignup />}
                    />
                    <Route path={Pages.Logout} element={<Logout />} />
                    <Route
                        path={Pages.AccessDenied}
                        element={<AccessDenied />}
                    />
                    <Route
                        path={Pages.SignupLinkedIn}
                        element={<SignUpLinkedIn />}
                    />
                    <Route
                        path={Pages.SignupLinkedInConfirm}
                        element={<SignUpLinkedInConfirm />}
                    />
                    <Route
                        path={Pages.ShareInviteFallback}
                        element={<ShareInviteFallback />}
                    />
                    {/* private routes */}
                    <Route
                        path={Pages.Roles}
                        element={
                            <PrivateRoute
                                allowedRoles={[UserRoles.TA, UserRoles.HM]}
                            >
                                <RoleList />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path={Pages.CreateNewRole}
                        element={
                            <PrivateRoute allowedRoles={[UserRoles.TA]}>
                                <CreateRole />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path={Pages.CreateNewRoleLLM}
                        element={
                            <PrivateRoute
                                allowedRoles={[UserRoles.TA, UserRoles.HM]}
                            >
                                <CreateRoleLLM />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path={Pages.UpdateRole}
                        element={
                            <PrivateRoute allowedRoles={[UserRoles.TA]}>
                                <UpdateRole />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path={Pages.Tenants}
                        element={
                            <PrivateRoute requireProductAdmin>
                                <Users />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path={Pages.Tenant}
                        element={
                            <PrivateRoute requireProductAdmin>
                                <ManageTenant />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path={Pages.CreateNewTenant}
                        element={
                            <PrivateRoute requireProductAdmin>
                                <CreateTenant />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path={Pages.CreateNewUser}
                        element={
                            <PrivateRoute requireProductAdmin>
                                <CreateUser />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path={Pages.UpdateUser}
                        element={
                            <PrivateRoute requireProductAdmin>
                                <UpdateUser />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path={Pages.ViewUser}
                        element={
                            <PrivateRoute requireProductAdmin>
                                <ViewUser />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path={Pages.ManageRole}
                        element={
                            <PrivateRoute
                                allowedRoles={[UserRoles.TA, UserRoles.HM]}
                            >
                                {/* Legacy route: redirect to ViewRole while preserving query string */}
                                <LegacyManageRoleRedirect />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path={Pages.ViewRole}
                        element={
                            <PrivateRoute
                                allowedRoles={[UserRoles.TA, UserRoles.HM]}
                            >
                                <ManageCandidateProvider>
                                    <ViewRoleRouter />
                                </ManageCandidateProvider>
                            </PrivateRoute>
                        }
                    />
                    {/* Explicit destinations */}
                    <Route
                        path={Pages.ManageRoleHM}
                        element={
                            <PrivateRoute allowedRoles={[UserRoles.HM]}>
                                <ManageCandidateProvider>
                                    <ManageRoleHM />
                                </ManageCandidateProvider>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path={Pages.ManageRoleTA}
                        element={
                            <PrivateRoute allowedRoles={[UserRoles.TA]}>
                                <ManageCandidateProvider>
                                    <ManageRoleTA />
                                </ManageCandidateProvider>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path={Pages.CandidateReview}
                        element={
                            <PrivateRoute allowedRoles={[UserRoles.HM]}>
                                <ManageCandidateProvider>
                                    <CandidateReview />
                                </ManageCandidateProvider>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path={Pages.DataManagement}
                        element={
                            <PrivateRoute requireProductAdmin>
                                <DataManagement />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path={Pages.HelixSearchV2}
                        element={
                            <PrivateRoute allowedRoles={[UserRoles.TA]}>
                                <ManageCandidateProvider>
                                    <HelixSearchV2Copy />
                                </ManageCandidateProvider>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path={Pages.HiringManagerKit}
                        element={
                            <PrivateRoute allowedRoles={[UserRoles.HM]}>
                                <HiringManagerKit />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path={Pages.LLMSettings}
                        element={
                            <PrivateRoute requireProductAdmin>
                                <LLMSettings />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path={Pages.Kit}
                        element={
                            <PrivateRoute requireProductAdmin>
                                <Kit />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path={Pages.KitConvoDebug}
                        element={
                            <PrivateRoute requireProductAdmin>
                                <KitConvoDebug />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path={Pages.KitTest}
                        element={
                            <PrivateRoute requireProductAdmin>
                                <KitTest />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path={Pages.JobScreeners}
                        element={
                            <PrivateRoute
                                allowedRoles={[UserRoles.TA, UserRoles.HM]}
                            >
                                <JobScreenersList />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path={Pages.JobScreener}
                        element={
                            <PrivateRoute
                                allowedRoles={[UserRoles.TA, UserRoles.HM]}
                            >
                                <JobScreenerDetail />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path={Pages.Jobs}
                        element={
                            <PrivateRoute requireProductAdmin>
                                <Jobs />
                            </PrivateRoute>
                        }
                    />
                    {/* Admin-only design-system test page */}
                    <Route
                        path="/llm/test"
                        element={
                            <PrivateRoute requireProductAdmin>
                                <Suspense fallback={<LoadingSpinner />}>
                                    <LlmTestPage />
                                </Suspense>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/llm/*"
                        element={
                            <PrivateRoute
                                allowedRoles={[UserRoles.TA, UserRoles.HM]}
                            >
                                <Suspense fallback={<LoadingSpinner />}>
                                    <LlmRoutes />
                                </Suspense>
                            </PrivateRoute>
                        }
                    />
                    <Route path="*" element={<NotFound404 />} />
                </Routes>
            </AuthProvider>
        </Router>
    )
}

function RedirectWithQuery({ to }: { to: string }): React.JSX.Element {
    const location = useLocation()
    const search = location.search ?? ''
    return <Navigate to={`${to}${search}`} replace />
}

function PrivateRoute({
    children,
    allowedRoles,
    requireProductAdmin = false,
}: {
    children: React.ReactNode
    allowedRoles?: UserRoles[]
    requireProductAdmin?: boolean
}): React.JSX.Element {
    const {
        user: authUser,
        loading: authLoading,
        error: authError,
        refresh,
    } = useAuth()
    const { userData, setUserData, clearUserData } = useUserDataStore()
    const navigate = useNavigate()
    const location = useLocation()
    const [getUserErrorStr, setGetUserErrorStr] = useState<string | undefined>()

    useEffect(() => {
        if (authLoading) return

        if (!authUser) {
            clearUserData()
            return
        }

        const fetchUserData = async () => {
            try {
                setGetUserErrorStr(undefined)
                const getLoggedInUserRes = await tenantApi.getLoggedInUser()

                const hasAdminRole = getLoggedInUserRes.user.isProductAdmin
                const tenantOverrideId = AuthHelper.getTenantOverrideId()

                if (tenantOverrideId && !hasAdminRole) {
                    const hasMembershipForOverride =
                        getLoggedInUserRes.user.memberships?.some(
                            (membership) =>
                                membership.tenantId === tenantOverrideId,
                        ) ?? false

                    if (!hasMembershipForOverride) {
                        AuthHelper.removeTenantOverrideId()
                        console.log(
                            'Cleared tenant override that user no longer has access to',
                        )
                    }
                }

                setUserData({
                    user: getLoggedInUserRes.user,
                    tenant: getLoggedInUserRes.tenant,
                })
            } catch (err: unknown) {
                console.error('Error fetching user:', err)
                clearUserData()

                if (axios.isAxiosError(err)) {
                    const status = err.response?.status
                    const rawMessage = (err.response?.data as any)?.message
                    const messages = Array.isArray(rawMessage)
                        ? rawMessage
                        : rawMessage
                          ? [rawMessage]
                          : []

                    const hasUnlinkedTenantMsg = messages.some(
                        (msg) =>
                            typeof msg === 'string' &&
                            (msg
                                .toLowerCase()
                                .includes('not linked to a tenant') ||
                                msg
                                    .toLowerCase()
                                    .includes('not linked to a person')),
                    )

                    if (status === 400 && hasUnlinkedTenantMsg) {
                        navigate(Pages.SignupLinkedIn, { replace: true })
                        return
                    }
                }

                const tenantOverrideId = AuthHelper.getTenantOverrideId()
                if (
                    tenantOverrideId &&
                    err instanceof Error &&
                    (err.message.includes('403') ||
                        err.message.includes('Forbidden') ||
                        err.message.includes('Only admins can override'))
                ) {
                    AuthHelper.removeTenantOverrideId()
                    console.log(
                        'Cleared problematic tenant override, retrying...',
                    )

                    try {
                        const retryRes = await tenantApi.getLoggedInUser()
                        setUserData({
                            user: retryRes.user,
                            tenant: retryRes.tenant,
                        })
                        return
                    } catch (retryError) {
                        console.error(
                            'Retry after clearing tenant override also failed:',
                            retryError,
                        )
                    }
                }

                if (err instanceof Error) {
                    if (err.message.includes('401')) {
                        await refresh()
                        navigate(Pages.Login)
                        return
                    }
                    setGetUserErrorStr(err.message)
                } else {
                    setGetUserErrorStr(JSON.stringify(err, null, 4))
                }
            }
        }

        if (!userData || userData.user.id !== authUser.id) {
            fetchUserData()
        } else {
            if (requireProductAdmin && !userData.user.isProductAdmin) {
                navigate(Pages.AccessDenied)
                return
            }

            if (
                allowedRoles &&
                allowedRoles.length > 0 &&
                !AuthHelper.userHasSomeRolesForTenant(
                    userData.user,
                    allowedRoles,
                    userData.tenant.id,
                )
            ) {
                navigate(Pages.AccessDenied)
            }
        }
    }, [
        authUser,
        authLoading,
        authError,
        refresh,
        userData,
        setUserData,
        clearUserData,
        allowedRoles,
        requireProductAdmin,
        navigate,
    ])

    if (authError) {
        return (
            <BlankLayout>
                <Note type={NoteType.ERROR}>
                    There was an error authenticating the user. Please refresh
                    to try again. Error details: {authError}
                </Note>
            </BlankLayout>
        )
    }

    if (authLoading) {
        return <LoadingSpinner />
    }

    if (getUserErrorStr) {
        return (
            <BlankLayout>
                <Note type={NoteType.ERROR}>
                    There was an error getting the user from the database.
                    Please refresh to try again. Error details:{' '}
                    {getUserErrorStr}
                </Note>
            </BlankLayout>
        )
    }

    // if we have a session user and matching cached DB user, we can render the private route
    if (authUser && userData && userData.user.id === authUser.id) {
        return <>{children}</>
    }

    if (!authUser) {
        const target = `${location.pathname}${location.search}`
        const loginUrl = `${Pages.Login}?target=${encodeURIComponent(target)}`
        return <Navigate to={loginUrl} replace />
    }

    return <LoadingSpinner />
}

function ViewRoleRouter(): React.JSX.Element {
    const [searchParams] = useSearchParams()
    const roleId = searchParams.get('id') || ''

    // Redirect quickly if no roleId
    if (!roleId) {
        return <Navigate to={Pages.Roles} replace />
    }

    // Decide destination based on role and candidateReviewStatus
    return <ViewRoleRouterInner roleId={roleId} />
}

function ViewRoleRouterInner({
    roleId,
}: {
    roleId: string
}): React.JSX.Element {
    const [dest, setDest] = useState<string | null>(null)
    const [error, setError] = useState<string | null>(null)
    const { userData } = useUserDataStore()

    useEffect(() => {
        let mounted = true
        ;(async () => {
            try {
                if (!userData) {
                    // wait until user data is available from PrivateRoute
                    return
                }

                const isTA = AuthHelper.userHasRoleForTenant(
                    userData.user,
                    UserRoles.TA,
                    userData.tenant.id,
                )
                const isHM = AuthHelper.userHasRoleForTenant(
                    userData.user,
                    UserRoles.HM,
                    userData.tenant.id,
                )

                if (isTA) {
                    if (mounted) setDest(Pages.ManageRoleTA + `?id=${roleId}`)
                    return
                }

                if (isHM) {
                    const res = await roleApi.getCandidateReviewStatus({
                        roleId,
                    })
                    const status = res.candidateReviewStatus
                    if (mounted) {
                        if (status !== CandidateReviewStatus.COMPLETE) {
                            setDest(Pages.CandidateReview + `?id=${roleId}`)
                        } else {
                            setDest(Pages.ManageRoleHM + `?id=${roleId}`)
                        }
                    }
                    return
                }

                // no matching role
                setError('No permitted role')
            } catch (e: any) {
                console.error('ViewRoleRouter error:', e)
                setError('Unable to open role')
            }
        })()
        return () => {
            mounted = false
        }
    }, [roleId, userData])

    if (error) return <Navigate to={Pages.Roles} replace />
    if (!userData || !dest) return <LoadingSpinner />
    return <Navigate to={dest} replace />
}

function LegacyManageRoleRedirect(): React.JSX.Element {
    const [searchParams] = useSearchParams()
    const qs = searchParams.toString()
    const dest = Pages.ViewRole + (qs ? `?${qs}` : '')
    return <Navigate to={dest} replace />
}

export default App
